import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Pencil, CalendarDays, Heart, Sparkles, BookOpen } from 'lucide-react';
import { useProfile } from '@/hooks/useProfile';

interface RamadanOnboardingProps {
  onComplete: () => void;
}

type Step = 'startDate' | 'support' | 'goals';

const goalPrompts = [
  { key: 'intention', label: 'My intention for this Ramadan…' },
  { key: 'habit', label: 'One habit I want to stay consistent with…' },
  { key: 'dua', label: 'A duʿāʾ I\'m carrying into this month…' },
];

export const RamadanOnboarding = ({ onComplete }: RamadanOnboardingProps) => {
  const { profile, updateProfile, isLoading } = useProfile();
  const [step, setStep] = useState<Step>('startDate');
  const [startDate, setStartDate] = useState('');
  const [goals, setGoals] = useState<Record<string, string>>({
    intention: '',
    habit: '',
    dua: '',
  });

  useEffect(() => {
    if (profile) {
      if (profile.ramadanStartDate) {
        setStartDate(profile.ramadanStartDate);
      }
      const savedGoals = profile.preferences?.ramadanGoals as Record<string, string> | undefined;
      if (savedGoals) {
        setGoals(prev => ({ ...prev, ...savedGoals }));
      }
    }
  }, [profile]);

  const handleConfirmDate = async () => {
    if (!startDate) return;
    await updateProfile({ ramadanStartDate: startDate });
    setStep('support');
  };

  const handleStartJourney = async () => {
    await updateProfile({
      preferences: {
        ...(profile?.preferences || {}),
        ramadanGoals: goals,
        hasCompletedRamadanOnboarding: true,
      },
    });
    onComplete();
  };

  const handleGoalChange = (key: string, value: string) => {
    setGoals(prev => ({ ...prev, [key]: value }));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-4xl text-gold">☽</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Header — matches dashboard style */}
        <motion.div
          className="text-center pt-10 pb-8"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-4xl mb-4 text-gold">☽</div>
          <h1 className="font-display text-2xl md:text-3xl text-ink tracking-tight leading-[1.2]">
            Your Ramadan Journey
            <br />
            Starts Here
          </h1>
          <div className="w-12 h-px bg-border mx-auto mt-6" />
        </motion.div>

        {/* Step content */}
        <AnimatePresence mode="wait">
          {step === 'startDate' && (
            <motion.div
              key="startDate"
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
              className="space-y-6"
            >
              {/* Card — matches dashboard card style */}
              <div className="bg-card border border-border/50 rounded-lg p-6 space-y-6">
                <div className="text-center space-y-2">
                  <p className="text-lg font-display text-ink">
                    When does your Ramadan begin?
                  </p>
                  <p className="text-sm text-muted-foreground">
                    We'll use this to guide your 30-day journey
                  </p>
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="ramadan-start"
                    className="text-xs uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2"
                  >
                    <CalendarDays className="w-3.5 h-3.5" />
                    Ramadan start date
                  </label>
                  <input
                    id="ramadan-start"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background text-foreground"
                  />
                </div>

                <button
                  onClick={handleConfirmDate}
                  disabled={!startDate}
                  className="w-full px-4 py-2.5 bg-primary text-primary-foreground rounded-md text-sm font-medium
                             transition-colors hover:bg-primary/90 disabled:opacity-40 disabled:pointer-events-none"
                >
                  Confirm start date
                </button>
              </div>
            </motion.div>
          )}

          {step === 'support' && (
            <motion.div
              key="support"
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
              className="space-y-6"
            >
              <div className="bg-card border border-border/50 rounded-lg p-6 space-y-6">
                <div className="text-center space-y-2">
                  <p className="text-lg font-display text-ink">
                    Support Layali
                  </p>
                  <p className="text-sm text-muted-foreground leading-relaxed max-w-sm mx-auto">
                    Layali is a labour of love — built to help Muslims deepen their Ramadan experience. Your support helps keep it free and growing.
                  </p>
                </div>

                <div className="space-y-3">
                  {[
                    {
                      icon: Heart,
                      title: 'Keep it free for everyone',
                      description: 'Your donation helps us keep Layali accessible to all.',
                      color: 'text-rose-500',
                      bg: 'bg-rose-50',
                    },
                    {
                      icon: Sparkles,
                      title: 'Fund new features',
                      description: 'Help us build more tools for spiritual growth.',
                      color: 'text-amber-500',
                      bg: 'bg-amber-50',
                    },
                    {
                      icon: BookOpen,
                      title: 'Sadaqah Jariyah',
                      description: 'An ongoing charity that benefits you and others.',
                      color: 'text-emerald-600',
                      bg: 'bg-emerald-50',
                    },
                  ].map((item, index) => (
                    <motion.div
                      key={item.title}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 + index * 0.08, duration: 0.35 }}
                      className={`${item.bg} rounded-lg p-4 flex items-start gap-3`}
                    >
                      <item.icon className={`w-5 h-5 ${item.color} mt-0.5 shrink-0`} />
                      <div>
                        <p className="text-sm font-medium text-ink">{item.title}</p>
                        <p className="text-xs text-muted-foreground">{item.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <a
                  href="https://buy.stripe.com/bJecN59MH0xv79x5xh14400"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm font-medium
                             transition-colors hover:opacity-90"
                  style={{
                    backgroundColor: 'hsl(45, 55%, 45%)',
                    color: 'hsl(30, 30%, 98%)',
                  }}
                >
                  <Heart className="w-4 h-4" />
                  Donate & Support
                </a>

                <button
                  onClick={() => setStep('goals')}
                  className="w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
                >
                  Skip for now →
                </button>
              </div>
            </motion.div>
          )}

          {step === 'goals' && (
            <motion.div
              key="goals"
              initial={{ opacity: 0, y: 35 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
              className="space-y-6"
            >
              {/* Card */}
              <div className="bg-card border border-border/50 rounded-lg p-6 space-y-6">
                <div className="text-center space-y-2">
                  <p className="text-lg font-display text-ink">
                    Set your Ramadan goals
                  </p>
                  <button
                    onClick={() => setStep('startDate')}
                    className="text-xs text-gold flex items-center gap-1 mx-auto hover:opacity-80 transition-opacity"
                  >
                    <Pencil className="w-3 h-3" />
                    Edit start date
                  </button>
                </div>

                <div className="space-y-5">
                  {goalPrompts.map((prompt, index) => (
                    <motion.div
                      key={prompt.key}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.08 + index * 0.08, duration: 0.35 }}
                      className="space-y-2"
                    >
                      <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                        {prompt.label}
                      </label>
                      <textarea
                        value={goals[prompt.key] || ''}
                        onChange={(e) => handleGoalChange(prompt.key, e.target.value)}
                        rows={2}
                        placeholder="Write here…"
                        className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background text-foreground
                                   placeholder:text-muted-foreground/50 resize-none
                                   focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                      />
                    </motion.div>
                  ))}
                </div>

                <button
                  onClick={handleStartJourney}
                  className="w-full px-4 py-2.5 bg-primary text-primary-foreground rounded-md text-sm font-medium
                             transition-colors hover:bg-primary/90"
                >
                  Start my journey
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
